import React, { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import toastr from 'toastr'
import filter from 'lodash/filter'
import findIndex from 'lodash/findIndex'
import parseCsv from '@Utils/ParseCsv'
import convertImportedTermbaseEntryData from '@Utils/ConvertImportedTermbaseEntryData'
import Button from '@SiteComponents/Button'
import Popup from '@SiteComponents/Popup'
import StepIndicator from '@SiteComponents/StepIndicator'
import UploadArea from '@SiteComponents/UploadArea'
import ColumnMapping from './ColumnMapping'
import Continue from '@Images/new_project_popup_continue.svg'
import Cancel from '@Images/new_project_popup_cancel.svg'
import Loader from '@Images/import_terms_popup_loader.svg'

import './ImportTermsPopup.scss'

import {
  closeImportTermsPopup,
} from '@SiteModules/Popup/actions'

import {
  importTermbaseEntries,
} from '@SiteModules/Termbase/actions'

import {
  getActiveTermbaseId,
  getActiveTermbaseLanguages,
  getActiveTermbaseFullnameLanguages,
  getModifyingTermbase,
} from '@SiteModules/Termbase/selectors'

const NO_SELECTION_OPTION = '-- No selection --'
const MAX_NUMBER_OF_ROWS = 3000

const ImportTermsPopup: React.FC= () => {
  const dispatch = useDispatch()

  const [isFirstStep, setIsFirstStep] = useState(true)
  const [dataHeader, setDataHeader] = useState<string[]>([])
  const [rows, setRows] = useState<string[][]>([])
  const [sourceLanguage, setSourceLanguage] = useState('')
  const [targetLanguage, setTargetLanguage] = useState('')
  const [sourceTermsColumnHeader, setSourceTermsColumnHeader] = useState('')
  const [translationTermsColumnHeader, setTranslationTermsColumnHeader] = useState('')
  const [sourcePronunciationColumnHeader, setSourcePronunciationColumnHeader] = useState('')
  const [targetPronunciationColumnHeader, setTargetPronunciationColumnHeader] = useState('')
  const [warningSourceTerms, setWarningSourceTerms] = useState<string | null>(null)
  const [warningSourceLanguage, setWarningSourceLanguage] = useState<string | null>(null)
  const [warningTranslationTerms, setWarningTranslationTerms] = useState<string | null>(null)
  const [warningTargetLanguage, setWarningTargetLanguage] = useState<string | null>(null)

  const fileRef = useRef<HTMLInputElement | null>(null)

  const activeTermbaseId = useSelector(getActiveTermbaseId)
  const activeTermbaseFullnameLanguages = useSelector(getActiveTermbaseFullnameLanguages)
  const activeTermbaseLanguages = useSelector(getActiveTermbaseLanguages)
  const modifyingTermbase = useSelector(getModifyingTermbase)

  let header = 'Upload your file'
  let stepName = 'File importation'
  let currentStep = 1

  if (!isFirstStep && !modifyingTermbase) {
    header = 'Map your CSV columns'
    stepName = 'Map columns'
    currentStep = 2
  } else if (!isFirstStep) {
    header = 'Processing your file'
    stepName = 'Processing file'
    currentStep = 3
  }

  const sourcePronunciationColumnSelected = sourcePronunciationColumnHeader.length > 0 && sourcePronunciationColumnHeader !== NO_SELECTION_OPTION
  const targetLanguageSelected = targetLanguage.length > 0 && targetLanguage !== NO_SELECTION_OPTION
  const translationTermsColumnSelected = translationTermsColumnHeader.length > 0 && translationTermsColumnHeader !== NO_SELECTION_OPTION
  const targetPronunciationColumnSelected = targetPronunciationColumnHeader.length > 0 && targetPronunciationColumnHeader !== NO_SELECTION_OPTION
  const hasWarnings = !!(warningSourceTerms || warningSourceLanguage || warningTranslationTerms || warningTargetLanguage)
  const shouldDisableCTAButton = hasWarnings || modifyingTermbase

  useEffect(() => {
    if (!!warningSourceLanguage) {
      setWarningSourceLanguage(null)
    }
  }, [sourceLanguage])

  useEffect(() => {
    if (!!warningSourceTerms) {
      setWarningSourceTerms(null)
    }
  }, [sourceTermsColumnHeader])

  useEffect(() => {
    if (!!warningTargetLanguage && sourceLanguage !== targetLanguage) {
      setWarningTargetLanguage(null)
    }
  }, [sourceLanguage, targetLanguage])

  useEffect(() => {
    if (!!warningTranslationTerms && (!targetPronunciationColumnSelected || translationTermsColumnSelected)) {
      setWarningTranslationTerms(null)
    }
  }, [translationTermsColumnHeader, targetPronunciationColumnHeader])

  const handleClose = () => {
    dispatch(closeImportTermsPopup())
  }

  const handleContinue = () => {
    if (!activeTermbaseId) { return }

    setWarnings()

    if (hasWarnings) { return }

    const sourceTermsColumnIndex = findIndex(dataHeader, data => data === sourceTermsColumnHeader)
    const sourcePronunciationColumnIndex = sourcePronunciationColumnSelected ? findIndex(dataHeader, data => data === sourcePronunciationColumnHeader) : null
    const translationTermsColumnIndex = translationTermsColumnSelected ? findIndex(dataHeader, data => data === translationTermsColumnHeader) : null
    const targetPronunciationColumnIndex = targetPronunciationColumnSelected ? findIndex(dataHeader, data => data === targetPronunciationColumnHeader) : null

    const sourceLanguageIndex = findIndex(activeTermbaseFullnameLanguages, language => language === sourceLanguage)
    const targetLanguageIndex = targetLanguageSelected ? findIndex(activeTermbaseFullnameLanguages, language => language === targetLanguage) : -1
    const sourceLanguageCode = activeTermbaseLanguages[sourceLanguageIndex]
    const targetLanguageCode = targetLanguageIndex >= 0 ? activeTermbaseLanguages[targetLanguageIndex] : ''

    const convertedData = convertImportedTermbaseEntryData(
      rows,
      sourceTermsColumnIndex,
      sourcePronunciationColumnIndex,
      translationTermsColumnIndex,
      targetPronunciationColumnIndex,
      sourceLanguageCode,
      targetLanguageCode
    )

    dispatch(importTermbaseEntries({ termbaseEntryImportData: convertedData, termbaseId: activeTermbaseId }))
  }

  const setWarnings = () => {
    if (sourceTermsColumnHeader.length === 0) {
      setWarningSourceTerms('Must be selected')
    } else if (sourceLanguage.length === 0) {
      setWarningSourceLanguage('Must be selected')
    } else if (sourceLanguage === targetLanguage) {
      setWarningTargetLanguage('Must differ from source language')
    } else if (targetPronunciationColumnSelected && !translationTermsColumnSelected) {
      setWarningTranslationTerms('Must be selected when importing target pronunciations')
    }
  }

  const openFileManager = () => {
    if (!fileRef) { return }
    if (!fileRef.current) { return }

    fileRef.current.click()
  }

  const handleFileDrop = (filesList: FileList) => {
    const files = Array.from(filesList).filter((item) => {
      return item.type.startsWith('text/csv')
    })

    if (files.length > 0) {
      handleUpload(files[0])
    }
  }

  const handleUploadClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) { return }

    handleUpload(file)
    e.target.value = '' // Set to an empty string to allow the upload of the same file again after aborting
  }

  const handleUpload = (file: File) => {
    const reader = new FileReader()

    reader.onload = e => {
      const text = e.target?.result as string
      if (!text) { return }

      const parsedRows = parseCsv(text)
      const newDataHeader = filter(parsedRows[0], data => data.length > 0) // Ignore empty header cells
      const newRows = parsedRows.slice(1)


      if (newRows.length > MAX_NUMBER_OF_ROWS) {
        toastr.error(`The file must have not more than ${MAX_NUMBER_OF_ROWS} rows.`, 'Error')
        return
      }

      setDataHeader(newDataHeader)
      setRows(newRows)
      setIsFirstStep(false)
    }

    reader.readAsText(file)
  }

  const renderSubheader = () => {
    return (
      <React.Fragment>
        {isFirstStep && (
          <div className="ImportTermsPopup__subheader">
            Upload your CSV file to start importing your terms. <br />
            There must be a unique header for each column in the first row of the file.
          </div>
        )}
        {!isFirstStep && !modifyingTermbase && (
          <div className="ImportTermsPopup__subheader">
            Match each column with the correct field in your term base. <br />
            Once mapped, your data will be added to your glossary.
          </div>
        )}
        {!isFirstStep && modifyingTermbase && (
          <div className="ImportTermsPopup__subheader">
            Your file is being uploaded and analyzed. Please wait a moment...
          </div>
        )}
      </React.Fragment>
    )
  }

  const renderBottom = () => {
    return (
      <div className="ImportTermsPopup__bottom">
        <div className="ImportTermsPopup__bottom-left">
          <Button
            caption="Cancel"
            icon={<Cancel />}
            onClick={handleClose}
            styleType="gray-subtle"
            size="small"
          />
        </div>
        <div className="ImportTermsPopup__bottom-right">
          <StepIndicator
            currentStep={currentStep}
            maxSteps={3}
            stepName={stepName}
          />
          {!isFirstStep && (
            <Button
              caption="Continue"
              icon={<Continue />}
              onClick={handleContinue}
              styleType="brand"
              size="small"
              iconRight
              disabled={shouldDisableCTAButton}
            />
          )}
        </div>
      </div>
    )
  }

  const renderUploadArea = () => {
    return (
      <React.Fragment>
        <UploadArea
          showAlert={false}
          handleFileDrop={handleFileDrop}
          openFileManager={openFileManager}
        />
        <input
          type="file"
          accept=".csv"
          ref={fileRef}
          style={{ display: 'none' }}
          onChange={handleUploadClick}
        />
      </React.Fragment>
    )
  }

  const renderColumnMapping = () => {
    return (
      <ColumnMapping
        dataHeader={dataHeader}
        termbaseLanguages={activeTermbaseFullnameLanguages}
        sourceLanguage={sourceLanguage}
        setSourceLanguage={setSourceLanguage}
        targetLanguage={targetLanguage}
        setTargetLanguage={setTargetLanguage}
        sourceTermsColumnHeader={sourceTermsColumnHeader}
        setSourceTermsColumnHeader={setSourceTermsColumnHeader}
        translationTermsColumnHeader={translationTermsColumnHeader}
        setTranslationTermsColumnHeader={setTranslationTermsColumnHeader}
        sourcePronunciationColumnHeader={sourcePronunciationColumnHeader}
        setSourcePronunciationColumnHeader={setSourcePronunciationColumnHeader}
        targetPronunciationColumnHeader={targetPronunciationColumnHeader}
        setTargetPronunciationColumnHeader={setTargetPronunciationColumnHeader}
        noSelectionOption={NO_SELECTION_OPTION}
        warningSourceTerms={warningSourceTerms}
        warningSourceLanguage={warningSourceLanguage}
        warningTranslationTerms={warningTranslationTerms}
        warningTargetLanguage={warningTargetLanguage}
      />
    )
  }

  const renderLoader = () => {
    return (
      <div className="ImportTermsPopup__loader">
        <Loader />
      </div>
    )
  }

  return (
    <Popup
      className="ImportTermsPopup"
      closePopup={handleClose}
    >
      <div className="ImportTermsPopup__header">
        {header}
      </div>
      {renderSubheader()}
      {isFirstStep && renderUploadArea()}
      {!isFirstStep && !modifyingTermbase && renderColumnMapping()}
      {!isFirstStep && modifyingTermbase && renderLoader()}
      {renderBottom()}
    </Popup>
  )
}

export default ImportTermsPopup
